/// <amd-module name="Release/Landing/LeftPanel/PipelinesStore" />

import { IVssPageContext } from "VSS/Platform/Context";

import { ReleaseDefinition, DefinitionEnvironmentReference, Folder } from "Release/Client/Contracts/Release";
import { StoreBase } from "DistributedTask/Flux/Store";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { PipelinesActions, ISearchDefinitionPayload } from "./PipelinesActions";
import { INode, NodeType } from "./AllPipelines/Types";
import { ActivePipelinesStore } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesStore";
import { PipelineSearchStore } from "Release/Landing/LeftPanel/PipelineSearch/PipelineSearchStore";
import { AllDefinitionsStore } from "Release/Landing/LeftPanel/AllPipelines/AllDefinitionsStore";
import { IActiveDefinitionReference } from "Release/Landing/DataProviderContracts";
import { ActiveDefinitionReferenceType } from "Release/Landing/DataProviderContracts";
import { getEnvironmentCurrentReleaseMap, getEnvironmentReferences } from "LandingUtils";
import { IDefinitionSelection } from "Selection/SelectionStore";
import { NamedStoreEvents } from "../Constants";

export interface IReleasesPivotInfo {
    releaseDefinitionId: number;
    releaseDefinitionName: string;
    showSelectPipeline: boolean;
    folderPath: string;
    environments: DefinitionEnvironmentReference[];
    definitionType: ActiveDefinitionReferenceType;
}

export class PipelinesStore extends StoreBase {
    public constructor(private _pageContext: IVssPageContext) {
        super("");
    }

    public static getKey(): string {
        return "release-landing-general-pipelines-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);

        this._searchText = "";
        this._activeDefinitions = null;
        this._recentDefinitions = null;
        this._searchedDefinitions = null;



        this._activePipelinesStore = StoreManager.GetStore<ActivePipelinesStore>(ActivePipelinesStore);
        this._pipelineSearchStore = StoreManager.GetStore<PipelineSearchStore>(PipelineSearchStore);
        this._allDefinitionsStore = StoreManager.GetStore<AllDefinitionsStore>(AllDefinitionsStore, "all-def-store", this._pageContext);

        this._allDefinitionsStore.addChangedListener(this._onAllDefinitionsStoreChange);
        this._pipelineSearchStore.addChangedListener(this._onSearchPipelineStoreChange);
        this._activePipelinesStore.addChangedListener(this._onActivePipelineStoreChange);

        this._activePipelinesStore.addListener(NamedStoreEvents.Pileline_Renamed_Moved_Event, this._onRenameOrMovePipeline);
    }

    public disposeInternal(): void {
        this._activePipelinesStore.removeListener(NamedStoreEvents.Pileline_Renamed_Moved_Event, this._onRenameOrMovePipeline);

        this._allDefinitionsStore.removeChangedListener(this._onAllDefinitionsStoreChange);
        this._pipelineSearchStore.removeChangedListener(this._onSearchPipelineStoreChange);
        this._activePipelinesStore.removeChangedListener(this._onActivePipelineStoreChange);
    }

    public getSelectedDefinitionFromActivePipelines(id: number): IActiveDefinitionReference | undefined {
        let selectedRd;
        if (this._activeDefinitions) {
            selectedRd = this._activeDefinitions.find((def) => def.id === id);
        }
        if (!selectedRd && this._recentDefinitions) {
            selectedRd = this._recentDefinitions.find((def) => def.id === id);
        }

        return selectedRd;
    }

    public getSelectedSearchedDefinition(id: number): ReleaseDefinition | undefined {
        let selectedRd;
        if (this._searchedDefinitions) {
            selectedRd = this._searchedDefinitions.find((def) => def.id === id);
        }

        return selectedRd;
    }

    public getSelectedDefinitionFromAllPipelines(id: number): IActiveDefinitionReference | undefined {
        let selectedRd;
        if (this._allDefinitions) {
            selectedRd = this._allDefinitions.find((def) => def.id === id);
        }

        return selectedRd;
    }

    public getDefinitionPath(id: number): string {
        let path: string = "";
        if (this._activeDefinitions) {
            const selectedRd = this._activeDefinitions.find((def) => def.id === id);
            if (selectedRd) {
                return selectedRd.path;
            }
        }

        if (this._recentDefinitions) {
            const selectedRd = this._recentDefinitions.find((def) => def.id === id);
            if (selectedRd) {
                return selectedRd.path;
            }
        }

        if (this._allDefinitions) {
            const selectedRd = this._allDefinitions.find((def) => def.id === id);
            if (selectedRd) {
                return selectedRd.path;
            }
        }

        return "";
    }

    public getHubStateFromSelection(selection: IDefinitionSelection): IReleasesPivotInfo {
        let definitionType;
        let environments;
        const showSearchResults = !!this._searchText;
        let selectedItem: IActiveDefinitionReference | ReleaseDefinition | undefined;
        if (!selection.folderId) {
            if (showSearchResults) {
                selectedItem = this.getSelectedSearchedDefinition(selection.id);
                definitionType = ActiveDefinitionReferenceType.SearchedResult;
                if (selectedItem) {
                    definitionType = ActiveDefinitionReferenceType.SearchedResult;
                    environments = getEnvironmentReferences(selectedItem);
                }
            }
            else {
                selectedItem = this.getSelectedDefinitionFromActivePipelines(selection.id);
                if (selectedItem) {
                    definitionType = selectedItem.definitionType;
                    environments = selectedItem.releasesData
                        ? selectedItem.releasesData.environments
                        : [];
                }
                else {
                    selectedItem = this.getSelectedDefinitionFromAllPipelines(selection.id);
                    if (selectedItem) {
                        definitionType = ActiveDefinitionReferenceType.Unknown;
                        environments = [];
                    }
                }
            }
        }

        return {
            releaseDefinitionId: selection.id,
            releaseDefinitionName: selectedItem ? selectedItem.name : "",
            showSelectPipeline: selection.noSelection,
            definitionType: definitionType || ActiveDefinitionReferenceType.Unknown,
            environments: environments || [],
            folderPath: selectedItem ? selectedItem.path : ""
        };
    }

    public getFolderPathFromSelection(selection: IDefinitionSelection): string {

        if (selection.folderId) {
            return selection.folderId;
        }

        const showSearchResults = !!this._searchText;
        let selectedItem: IActiveDefinitionReference | ReleaseDefinition | undefined;
        if (showSearchResults) {
            selectedItem = this.getSelectedSearchedDefinition(selection.id);
        }
        else {
            selectedItem = this.getSelectedDefinitionFromActivePipelines(selection.id) || this.getSelectedDefinitionFromAllPipelines(selection.id);
        }

        return (selectedItem ? selectedItem.path : "");
    }

    public isActiveListEmptied(): boolean {
        return this._emptyActive;
    }

    public getFolder(path: string): Folder | null | undefined {
        return this._allFolders && this._allFolders.find(f => f.path === path);
    }

    private _onAllDefinitionsStoreChange = (): void => {
        const allDefinitions: IActiveDefinitionReference[] = [];
        const allFolders: Folder[] = [];
        const allDefinitionsState = this._allDefinitionsStore.getState();
        Promise.all(Object.keys(allDefinitionsState).map((folder) => Promise.resolve(allDefinitionsState[folder])).filter((promise) => !!promise)).then((nodesList) => {
            nodesList.forEach((nodes) => {
                nodes.forEach((node) => {
                    if (node.type === NodeType.ReleaseDefinition && node.releaseDefinition.id) {
                        if (!allDefinitions.some((def) => def.id === node.releaseDefinition.id)) {
                            allDefinitions.push(node.releaseDefinition);
                        }
                    }
                    else if (node.type === NodeType.Folder && node.folder.path !== "\\") {
                        if (!allFolders.some((f) => f.path === node.folder.path)) {
                            allFolders.push(node.folder);
                        }
                    }
                });
            });

            if (!this._allDefinitions) {
                this._allDefinitions = allDefinitions;
            }
            else {
                this._allDefinitions.length = 0;
                this._allDefinitions.push(...allDefinitions);
            }

            if (!this._allFolders) {
                this._allFolders = allFolders;
            }
            else {
                this._allFolders.length = 0;
                this._allFolders.push(...allFolders);
            }
        });
    }

    private _onSearchPipelineStoreChange = (): void => {
        const pipelinesSearchState = this._pipelineSearchStore.getState();
        this._searchText = pipelinesSearchState.searchText;
        this._searchedDefinitions = pipelinesSearchState.searchedDefinitions;
    }

    private _onActivePipelineStoreChange = (): void => {
        const activePipelinesState = this._activePipelinesStore.getState();
        this._activeDefinitions = activePipelinesState.activeDefinitions;
        this._recentDefinitions = activePipelinesState.recentDefinitions;

        // If the active section is null, it implies data is being fetched. If it is empty, it means that all the definitions are deleted
        if (!!this._activeDefinitions && !this._activeDefinitions.length && !!this._recentDefinitions && !this._recentDefinitions.length) {
            if (!this._emptyActive /*emit only when there is an modification to this._emptyActive */) {
                this._emptyActive = true;
                this.emitChanged();
            }
        }
        else if (this._emptyActive) {
            this._emptyActive = false;
            this.emitChanged();
        }
    }

    private _onRenameOrMovePipeline = () => {
        this.emit(NamedStoreEvents.Pileline_Renamed_Moved_Event, {});
    }

    private _activePipelinesStore: ActivePipelinesStore;
    private _pipelineSearchStore: PipelineSearchStore;
    private _allDefinitionsStore: AllDefinitionsStore;
    private _allDefinitions: IActiveDefinitionReference[] | null;
    private _allFolders: Folder[] | null;
    private _activeDefinitions: IActiveDefinitionReference[] | null;
    private _recentDefinitions: IActiveDefinitionReference[] | null;
    private _searchedDefinitions: ReleaseDefinition[] | null;
    private _searchText: string;
    private _emptyActive: boolean = false;
}
